<template>
    <div :class="[ 'kaseya-send-company' ]">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Send Company Info To Kaseya</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="company-step" v-if="currentStep === 'company'">
                    <div v-if="company.errors.length > 0" class="row">
                        <div class="warnings col-md-12">
                            <red-warning-svg></red-warning-svg>
                            <p v-for="error in company.errors" :key="error" v-html="error"></p>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <app-border-section topText="Determine how your data will be sent">
                                    <app-input-radio v-model="company.selectedOption" :options="options" />
                            </app-border-section>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <app-border-section :topText="`What information from ${ company.name } would you like sent to Kaseya?`">
                                <div class="upload-options">
                                    <div class="upload-option">
                                        <app-switch v-model="company.shouldSendInformation" size="large">
                                            <span class="switch-label">
                                                <profile-card-svg :gradient="{ 'from': '#4bc9fe', 'to': '#5052fe' }"/>
                                                Company/About information
                                            </span>
                                        </app-switch>
                                    </div>
                                </div>
                            </app-border-section>
                        </div>
                    </div>
                    <div class="row" v-if="contacts.length > 0">
                        <div class="col-md-12">
                            <app-border-section :topText="`What information from ${ company.name } would you like sent to Kaseya?`">
                                <div class="upload-options">
                                    <div class="upload-option" v-for="contact in contacts" :key="contact.id">
                                        <app-switch v-model="contact.isSelected" size="large">
                                            <span class="switch-label">
                                                <div class="profile-image" :style="{ 'background-image': `url('${ contact.profileImageUrl }')` }"></div>
                                                {{ contact.name }}
                                            </span>
                                        </app-switch>
                                    </div>
                                </div>
                            </app-border-section>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <kaseya-send-contact :company="company" :contact="selectedContacts[currentContactIndex]" :index="currentContactIndex + 1" :count="selectedContacts.length" />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" v-if="currentStep === 'contacts'" :onClick="goBack">
                        Back
                    </app-button>
                    <app-button v-else class="transparent-btn" :onClick="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button class="transparent-btn skip-btn" v-if="currentStep === 'contacts' && selectedContacts.length > 1 && currentContactIndex < selectedContacts.length - 1" :onClick="skipContact" :disabled="isSendingContact" :loading="isSendingContact">
                        Next
                    </app-button>
                    <app-button v-if="currentStep === 'contacts'" :onClick="processExportRequest" :disabled="isSendingContact || this.selectedContacts[this.currentContactIndex].hasSendError" :loading="isSendingContact">
                        Send Contact to Kaseya
                    </app-button>
                    <app-button v-else :onClick="processExportRequest" :disabled="isSendingCompany || deactivatedSwitches" :loading="isSendingCompany">
                        {{ areAnyContactsSelected ? 'Next Step' : 'Send Company to Kaseya' }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppBorderSection = () => import('@/components/common/app-border-section');
const AppButton = () => import('@/components/common/app-button');
const AppInputRadio = () => import('@/components/common/app-input-radio');
const AppSwitch = () => import('@/components/common/app-switch');
const ProfileCardSvg = () => import('@/components/svg/profile-card-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const KaseyaSendContact = () => import('@/components/integration/kaseya-send-contact');
const RedWarningSvg = () => import('@/components/svg/red-warning-svg');

import EventBus from '@/scripts/event-bus.js';
import log from '@/scripts/logger';

export default {
    name: 'kaseya-send-company-widget',

    components: {
        'app-input-radio': AppInputRadio,
        'app-switch': AppSwitch,
        'profile-card-svg': ProfileCardSvg,
        'app-border-section': AppBorderSection,
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'kaseya-send-contact': KaseyaSendContact,
        'red-warning-svg': RedWarningSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            options: [
                { 
                    name: 'Only send new records',
                    value: 'insert'
                },
                {
                    name: 'Send all records, updating existing records in Kaseya',
                    value: 'upsert'
                }
            ],

            contacts: [],
            company: {
                id: null,
                name: null,
                clientId: null,
                kaseyaAccountId: null,
                imageUrl: null,
                selectedOption: null, 
                shouldSendInformation: null,
                errors: []
            },

            currentStep: 'company',
            currentContactIndex: 0,
            isSendingCompany: false,
            isSendingContact: false,
            deactivatedSwitches: false,

            kaseyaExtraInfo: {
                serverUrl: null,
                contactId: null
            },
        }
    },

    computed: {
        areAnyContactsSelected() {
            return this.contacts.some(contact => contact.isSelected);
        },

        selectedContacts() {
            if (this.areAnyContactsSelected) {
                return this.contacts.filter(contact => contact.isSelected);
            } else {
                return [];
            }
        },

        kaseyaAccLink: function() {
            return `${ this.kaseyaExtraInfo.serverUrl }/MSP/ClientUpdate.aspx?ID=${this.company.kaseyaAccountId}`;
        },
    },

    watch: {
        'company.shouldSendInformation': function() {
            this.deactivatedSwitches = !(this.company.shouldSendInformation
                || this.areAnyContactsSelected);
        },

        'areAnyContactsSelected': function() {
            this.deactivatedSwitches = !(this.company.shouldSendInformation
                || this.areAnyContactsSelected);
        }
    },

    created() {
        this.getData();
    },
    
    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getCompany()
            .then(this.getIntegrationInformation)
            .then(() => {
                if (this.company.id) {
                    this.getContacts();
                }
            })
            .catch(error => {
                this.$helpers.showFailNotification(error, { error });
            });
        },

        getCompany() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Companies?$filter=Id eq ${this.widget.options.id }`)
                    .then(response => {
                        this.createCompany(response.data.value[0]);
                        resolve();
                    })
                    .catch((error) => {
                        log.error('An error occurred while fetching your company', error);
                        reject();
                        this.isProcessing = false;
                    });
            })
        },

        getIntegrationInformation() {
            this.$api
                .get(`/ms/kaseya-api/v1/credentials/${ this.company.clientId }`)
                .then(response => {
                    if(response.data) {
                        this.kaseyaExtraInfo.serverUrl = response.data.data.ServerUrl;
                    }
            });
        },

        createCompany(companyResponse) {
            this.company.id = companyResponse.Id;
            this.company.name = companyResponse.Name;
            this.company.clientId = companyResponse.ClientId;
            this.company.kaseyaAccountId = companyResponse.KaseyaAccountId;
            this.company.imageUrl = companyResponse.LogoUrl || require('@/assets/images/company-avatar.png');
            this.company.selectedOption = this.options[1];
            this.company.shouldSendInformation = false;
        },

        getContacts() {
            this.$api
                .get(`/service/Contacts?$filter=CompanyId eq ${ this.company.id }`)
                .then(response => {
                    this.createContacts(response.data.value);
                    this.isProcessing = false;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your contacts.', { error });
                    this.isProcessing = false;
                });
        },

        createContacts(contactsResponse) {
            this.contacts = contactsResponse.map((contact) => {
                return {
                    id: contact.Id,
                    name: this.$helpers.getName(contact.FirstName, contact.LastName),
                    profileImageUrl: contact.ProfileImageUrl || require('@/assets/images/avatar.png'),
                    shouldSendInformation: true,
                    shouldSendActivities: false,
                    shouldSendOpportunities: false,
                    hasSendError: false,
                    selectedOption: 'upsert',
                    isSelected: false
                }
            });
        },

        goToSendContactsStep() {
            this.currentStep = 'contacts';
        },

        sendCompanyToKaseya() {
            this.isSendingCompany = true;
            this.postCompany()
        },

        postCompany() {
            this.company.errors = [];

            var request = {
                isUpsert: this.company.shouldSendInformation
            };

            this.$api
                .post(`/ms/kaseya-api/v1/integrations/export/companies/${ this.company.id }/${ this.company.clientId }`, request)
                .then(this.updateCompanyWithKaseyaAccId)
                .then((value) => {
                    if (this.selectedContacts.length > 0) {
                        this.processCompanyUpdateSuccess();
                    } else {
                        this.closeWidget();
                    }

                    EventBus.$emit('company:update');
                    this.isSendingCompany = false;

                    this.createCompanySuccessMessage(value);
                })
                .catch(error => {
                    this.isSendingCompany = false;
                    this.company.errors.push(error.response.data.Message);
            });
        },

        createCompanySuccessMessage(kaseyaAcc) {
            var actionTaken = this.company.selectedOption.value == 'upsert' ? 'updated' : 'inserted';
            var message = `<strong>${ this.company.name }</strong> has been ${ actionTaken } in Kaseya.`;
            const link = `${ this.kaseyaExtraInfo.serverUrl }/MSP/ClientUpdate.aspx?ID=${kaseyaAcc}`
            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link,
                action: null
            });
        },

        processCompanyUpdateSuccess() {
            this.updateCompanyWithKaseyaAccId()
            .then(() => {
                this.goToSendContactsStep();
            })
            .catch(response => {
                this.$helpers.showFailNotification(response, { error: response });
            });
        },

        updateCompanyWithKaseyaAccId() {
            return new Promise((resolve, reject) => {
                this.$api.get(`/service/Companies?$filter=Id eq ${ this.company.id } &$select=KaseyaAccountId`)
                .then(response => {
                    this.company.kaseyaAccountId = this.company.KaseyaAccountId || response.data.value[0].KaseyaAccountId;
                    resolve(response.data.value[0].KaseyaAccountId);
                })
                .catch(response => {
                    reject(response);
                });
            });
        },

        sendCurrentContactToKaseya() {
            this.isSendingContact = true;
            var currentContact = this.selectedContacts[this.currentContactIndex];

            var clientId = this.company.clientId;
            var contactId = currentContact.id;

            var request = {
                isUpsert: this.company.selectedOption.value == 'upsert',
                exportContacts: currentContact.shouldSendInformation,
                exportDeals: currentContact.shouldSendOpportunities,
                exportActivities: currentContact.shouldSendActivities
            };

            this.$api
                .post(`/ms/kaseya-api/v1/integrations/export/contacts/${ contactId }/${ clientId }`, request)
                .then(() => {
                    if(this.currentContactIndex == this.selectedContacts.length - 1) {
                        this.closeWidget();
                    }

                    this.processSentContact(contactId);
                })
                .catch((error) => {
                    this.isSendingContact = false;
                    this.selectedContacts[this.currentContactIndex].hasSendError = true;
                });
        },

        processSentContact(contactId) {
            this.currentContactIndex++
            this.exportedContactCount++;
            this.isSendingContact = false;

            var contact = {
                id: contactId,
                name: null,
                kaseyaAccountId: null
            };

            this.hydrateSentContact(contact)
            .then((kaseyaResourceLink) => {
                
                this.createContactSuccessMessage(kaseyaResourceLink, contact);
            })

        },

        hydrateSentContact(contact) {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts/?$filter=Id eq ${ contact.id } &$select=FirstName,LastName,KaseyaAccountId,KaseyaContactId`)
                    .then(response => {
                        var kaseyaResourceLink = `${this.kaseyaExtraInfo.serverUrl}/CRM/ContactEdit.aspx?ID=${response.data.value[0].KaseyaContactId}`;
                        contact.name = this.$helpers.getName(response.data.value[0].FirstName, response.data.value[0].LastName);
                        contact.kaseyaAccountId = response.data.value[0].KaseyaAccountId;

                        resolve(kaseyaResourceLink);
                    })
                    .catch((error) => {
                        reject('An error occurred while fetching your Kaseya integration info.');
                });
            })
        },

        createContactSuccessMessage(kaseyaLink, contact) {
            var actionTaken = this.company.selectedOption.value == 'upsert' ? 'updated' : 'inserted';
            var message = `<strong>${ contact.name }</strong> has been ${ actionTaken } in Kaseya.`;

            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: kaseyaLink,
                action: null
            });
        },

        skipContact() {
            this.currentContactIndex++;
        },

        goBack() {
            if (this.currentContactIndex !== 0) {
                this.currentContactIndex--;
            } else {
                this.currentStep = 'company';
            }
        },

        processExportRequest() {
            if(this.currentStep == 'company') {
                this.processCompanyStep();
            }
            else {
                this.processContactStep();
            }
        },

        processCompanyStep() {
            if(!this.company.shouldSendInformation && this.areAnyContactsSelected) {
                this.currentStep = 'contacts';
            }
            else {
                this.sendCompanyToKaseya();
            }
        },

        processContactStep() {
            this.sendCurrentContactToKaseya();
        }
    },

    
}
</script>

<style>
.kaseya-send-company .transparent-btn {
    background: transparent;
    color: #877D88;
}

.kaseya-send-company .transparent-btn:hover {
    background: #E1E0E5
}

.kaseya-send-company .action-container {
    display: flex;
}

.kaseya-send-company .skip-btn {
    margin-right: 15px;
}
    
.kaseya-send-company .warnings strong {
    color: #F30070;
}

</style>

<style scoped>
.kaseya-send-company .content-wrapper {
    padding-left: 0;
    padding-right: 0;
}

.kaseya-send-company .row {
    margin-bottom: 30px;
}

.kaseya-send-company .app-switch {
    justify-content: flex-start;
}

.kaseya-send-company .app-switch .switch-label {
    display: flex;
    align-items: center;
}

.kaseya-send-company .app-switch .switch-label svg {
    width: 28px;
    margin-right: 13px;
}

.kaseya-send-company .app-switch .switch-label .profile-image {
    width: 48px;
    height: 48px;
    margin-right: 12px;
    border-radius: 50%;
    background-size: cover;
    background-repeat: no-repeat;
}

.kaseya-send-company .warnings {
    display: flex;
    align-items: center;
    color: #F30070;
    font-size: 14px;
    max-width: 550px;
    padding-top: 5px;
}

.kaseya-send-company .warnings p {
    margin-left: 15px;
    margin-bottom: 0;
}

</style>